import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCode, FiX, FiInfo, FiCopy } from 'react-icons/fi';
import { toast } from 'react-toastify';
import SqlSourceView from './SqlSourceView';
import { monoWideText } from '../../utils/monoWide';
import './QueryView.css';

/**
 * 「쿼리 보기」 단추 — 모니터링 창 전부가 같은 모양을 쓴다(2026-10-27 사용자 요청: 자원·세션·SQL 성능·AWR·오라클 성능 분석이
 * 아이콘만 / 아이콘+「조회 쿼리 보기」 / 「SQL」 로 제각각이었다). 모양·문구를 창마다 다시 칠하지 말 것.
 */
export function QueryViewButton({ onClick, disabled = false, title }) {
    const { t } = useTranslation();
    return (
        <button type="button" className="qv-btn" onClick={onClick} disabled={disabled}
            title={title || t('monitoring:qv.hint')} aria-label={title || t('monitoring:qv.hint')}>
            <FiCode size={11} /> {t('monitoring:qv.button')}
        </button>
    );
}

const bindText = (v) => (v == null ? 'NULL' : typeof v === 'string' ? `'${v}'` : String(v));

/**
 * 「쿼리 보기」 창 — 한 문장(sql) 또는 그 조회가 실제로 실행한 문장 여럿(items: [{ sql, binds, label? }]).
 * 문장은 늘 서버가 준 실제 SQL 이다(사본을 화면에 두지 않는다). 바인드가 있으면 값과 함께 보인다.
 */
export function QueryViewDialog({ title, sql, error, items, note, loadingText, onClose }) {
    const { t } = useTranslation();
    const panelRef = useRef(null);
    useEffect(() => { if (panelRef.current) panelRef.current.focus(); }, []);
    const list = Array.isArray(items) ? items.filter(it => it && typeof it.sql === 'string') : null;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    return (
        <div className="qv-overlay" onClick={onClose}>
            <div ref={panelRef} tabIndex={-1} className="app-modal qv-panel" data-app-modal role="dialog" aria-label={title}
                onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
                <div className="app-modal-header qv-header">
                    <span className="app-modal-title"><FiCode size={14} /> {title}</span>
                    <button type="button" className="app-modal-close" onClick={onClose} aria-label={t('common:ui.close')}>
                        <FiX size={16} />
                    </button>
                </div>
                <div className="qv-scroll">
                    {list ? (list.length ? list.map((it, i) => (
                        <div key={i} className="qv-stmt">
                            <div className="qv-stmt-head">
                                {list.length > 1 && <span className="qv-stmt-no">{t('monitoring:qv.stmt', { n: i + 1, total: list.length })}</span>}
                                {it.label && <span className="qv-stmt-label">{it.label}</span>}
                            </div>
                            <SqlSourceView className="qv-body" sql={it.sql} loadingText="" />
                            {it.binds && typeof it.binds === 'object' && Object.keys(it.binds).length > 0 && (
                                <div className="qv-binds">
                                    <span className="qv-binds-title">{t('monitoring:qv.binds')}</span>
                                    {Object.entries(it.binds).map(([k, v]) => (
                                        <code key={k}>:{k} = {bindText(v)}</code>
                                    ))}
                                </div>
                            )}
                        </div>
                    )) : <div className="qv-empty">{t('monitoring:qv.none')}</div>) : (
                        <SqlSourceView className="qv-body" sql={typeof sql === 'string' ? sql : null} error={error}
                            loadingText={loadingText || t('monitoring:qv.loading')} />
                    )}
                </div>
                <div className="qv-note"><FiInfo size={12} /> {note || t('monitoring:qv.note')}</div>
            </div>
        </div>
    );
}

/**
 * 글 보기 창 — 실행계획(DBMS_XPLAN) · SQL Monitor 리포트(TEXT)처럼 **고정폭 표**를 그대로 보인다(쿼리 보기 창과 같은 틀).
 * 한글은 monoWideText 로 2칸 상자에 넣는다(넣지 않으면 그 줄만 밀린다). meta = 제목 아래 한 줄(읽은 곳·노드 등), notes = 못 읽은 까닭.
 */
export function TextViewDialog({ title, icon, text, loading, error, meta, notes, onClose }) {
    const { t } = useTranslation();
    const panelRef = useRef(null);
    useEffect(() => { if (panelRef.current) panelRef.current.focus(); }, []);
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    const copy = () => {
        if (!text || !navigator.clipboard) return;
        navigator.clipboard.writeText(text).then(() => toast.success(t('monitoring:qv.copied')), () => toast.error(t('monitoring:qv.copyFailed')));
    };
    return (
        <div className="qv-overlay" onClick={onClose}>
            <div ref={panelRef} tabIndex={-1} className="app-modal qv-panel qv-panel-wide" data-app-modal role="dialog" aria-label={title}
                onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
                <div className="app-modal-header qv-header">
                    <span className="app-modal-title">{icon} {title}</span>
                    {text && (
                        <button type="button" className="qv-btn qv-copy" onClick={copy} title={t('monitoring:qv.copy')}>
                            <FiCopy size={11} /> {t('monitoring:qv.copy')}
                        </button>
                    )}
                    <button type="button" className="app-modal-close" onClick={onClose} aria-label={t('common:ui.close')}>
                        <FiX size={16} />
                    </button>
                </div>
                {meta && <div className="qv-meta">{meta}</div>}
                <div className="qv-scroll">
                    {loading ? <div className="qv-empty">{t('monitoring:qv.loading')}</div>
                        : error ? <div className="qv-empty qv-error">{error}</div>
                            : text ? <pre className="qv-body">{monoWideText(text)}</pre>
                                : <div className="qv-empty">{t('monitoring:qv.noText')}</div>}
                    {!loading && (notes || []).length > 0 && (
                        <ul className="qv-notes">{notes.map(n => <li key={n}>{n}</li>)}</ul>
                    )}
                </div>
            </div>
        </div>
    );
}
